import { authedGet } from '../../api/http';
import { buildQuery } from '../../api/query';
import type { CatalogSearchResponse } from '../../data/catalogSearch';
import type { PublishedPrices } from '../../lib/contentPrice';
import type { RawCatalogEntry } from '../../lib/catalogMap';
import { isListedSku, platformIdOf } from '../../lib/catalogPlatform';
import { validateCatalogAuthority } from './editorial';
import { readDiscoveryList, readDiscoverySearch, readSoldApis } from './discoveryReader';
import { prerenderInput } from '../prerender/buildInput';

export const CATALOG_FRESH_TIME = 5 * 60 * 1_000;

export interface CatalogSearchOptions {
  category?: string;
  platform?: string;
  limit?: number;
}

/** The sole authoritative catalog snapshot, as the gateway sends it. */
const fetchCatalogWire = async (): Promise<unknown> =>
  prerenderInput('catalog') ?? await authedGet<unknown>('/catalog');

/** The listed catalog every projection reads. */
export const fetchCatalogEntries = async (): Promise<RawCatalogEntry[]> => {
  const entries = readDiscoveryList(await fetchCatalogWire());
  validateCatalogAuthority(entries);
  return entries;
};

/**
 * The catalog reduced to each SKU's published pricing, for pages that quote
 * prices and show nothing else of it. Measured on 2026-09-27: 64 KB of JSON
 * against 574 KB for the full catalog, and whatever a loader fetches is
 * dehydrated into that page's HTML.
 */
export const fetchPublishedPrices = async (): Promise<PublishedPrices> =>
  Object.fromEntries(readSoldApis(await fetchCatalogWire()).map((entry) => [entry.slug, entry.pricing]));

/** The most results the gateway returns, and its count for a queried search
 *  that names no limit (`maximumSearchLimit` and `defaultSearchLimit` in
 *  server/internal/discovery/search.go:13-14). */
const GATEWAY_MAXIMUM_LIMIT = 50;
const GATEWAY_DEFAULT_LIMIT = 25;

/**
 * Ranked search without the unlisted SKUs. The gateway cuts its list to the
 * limit before we can drop them, so this asks for its maximum, drops them, and
 * then cuts to the caller's limit. `total` loses only the unlisted results among
 * those returned: exact while the gateway matches at most its maximum, and an
 * overcount past that by however many unlisted SKUs ranked below it.
 */
export const searchCatalogRanked = async (
  query: string,
  options?: CatalogSearchOptions,
): Promise<CatalogSearchResponse> => {
  const querystring = buildQuery({
    q: query,
    category: options?.category,
    platform: options?.platform,
    limit: GATEWAY_MAXIMUM_LIMIT,
  });
  const response = readDiscoverySearch(await authedGet<unknown>(`/catalog/search${querystring}`));
  const listed = response.results.filter((result) => isListedSku(result.slug));
  return {
    ...response,
    results: listed.slice(0, options?.limit ?? GATEWAY_DEFAULT_LIMIT),
    total: response.total - (response.results.length - listed.length),
  };
};

export const searchCatalogIds = async (query: string): Promise<string[]> => {
  const response = await searchCatalogRanked(query);
  const ids: string[] = [];
  for (const entry of response.results) {
    const id = platformIdOf(entry.slug);
    if (!ids.includes(id)) ids.push(id);
  }
  return ids;
};
